import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, BadgeCheck, FileCheck2, MapPin, ArrowRight } from "lucide-react";
import LiveLayout from "./LiveLayout";
import D13Footer from "../preview/D13Footer";
import { Star } from "./Star";
import { SITE_UPDATED } from "../../lib/adelContent";
import "./Editorial.css";

/* /trust — licensing & verification. The biggest trust lever in the Kenya→Gulf
   niche: a verifiable NEA licence + ASMAK membership + how to verify us + scam
   awareness. Neutral page (both audiences link here from the footer). */
export default function TrustPage() {
  return (
    <LiveLayout audience="candidate">
      <header className="ed-band ed-band--cream spoke-head">
        <div className="ed-container">
          <span className="ed-eyebrow spoke-head__eyebrow"><Star className="ed-eyebrow__star" /> Licensed &amp; verified</span>
          <h1 className="spoke-title">A licensed agency <em>you can verify.</em></h1>
          <p className="ed-lede spoke-intro">
            Adel Group (Adel Abdelrahman Group Limited) is a licensed Kenyan recruitment agency.
            Don&rsquo;t take our word for it — here&rsquo;s how to confirm it yourself, and how we
            help you stay safe from recruitment scams.
          </p>
          <p className="ed-stamp">Last updated {SITE_UPDATED}</p>
        </div>
      </header>

      {/* Credentials */}
      <section className="ed-band ed-band--sand ed-pad">
        <div className="ed-container">
          <div className="trust-grid">
            <div className="trust-card">
              <span className="trust-card__icon"><ShieldCheck size={22} strokeWidth={2} /></span>
              <h3>Licensed by the NEA</h3>
              <p>We hold a private employment agency licence from Kenya&rsquo;s National Employment Authority. Licence <span className="trust-card__id">RA/2025/09/56</span>.</p>
            </div>
            <div className="trust-card">
              <span className="trust-card__icon"><BadgeCheck size={22} strokeWidth={2} /></span>
              <h3>ASMAK member</h3>
              <p>We are a listed member of ASMAK, the Kenyan recruitment-agencies association, on their public members register.</p>
            </div>
            <div className="trust-card">
              <span className="trust-card__icon"><BadgeCheck size={22} strokeWidth={2} /></span>
              <h3>No registration fee</h3>
              <p>We never charge jobseekers a registration fee to begin. A licensed agency does not ask you to pay to register.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How to verify us */}
      <section className="ed-band ed-band--cream ed-pad">
        <div className="ed-container">
          <span className="ed-eyebrow"><Star className="ed-eyebrow__star" /> How to verify us</span>
          <h2 className="ed-h2">Check us for yourself, <em>in three ways.</em></h2>
          <ul className="trust-steps">
            <li><FileCheck2 size={18} strokeWidth={2} /> <span>Look up licence <strong>RA/2025/09/56</strong> on the National Employment Authority register (neaims.go.ke).</span></li>
            <li><BadgeCheck size={18} strokeWidth={2} /> <span>We are a listed member of ASMAK — the Kenyan recruitment-agencies association.</span></li>
            <li><MapPin size={18} strokeWidth={2} /> <span>Visit our office: Room 12, 6th floor, View Park Towers, Utalii Lane, Nairobi.</span></li>
          </ul>
        </div>
      </section>

      {/* Scam awareness */}
      <section className="ed-band ed-band--sand ed-pad">
        <div className="ed-container">
          <span className="ed-eyebrow"><Star className="ed-eyebrow__star" /> Stay safe</span>
          <h2 className="ed-h2">Protect yourself from <em>recruitment scams.</em></h2>
          <ul className="trust-steps">
            <li><ShieldCheck size={18} strokeWidth={2} /> <span>A licensed agency never asks jobseekers for a registration fee. If someone does, walk away.</span></li>
            <li><ShieldCheck size={18} strokeWidth={2} /> <span>Always confirm an agency&rsquo;s NEA licence on the official register before you pay for anything.</span></li>
            <li><ShieldCheck size={18} strokeWidth={2} /> <span>Read and understand your contract — pay, hours, days off — before you sign anything.</span></li>
            <li><ShieldCheck size={18} strokeWidth={2} /> <span>A real job means a real employer and a real interview. You should always know who you will work for.</span></li>
          </ul>
        </div>
      </section>

      {/* Close */}
      <section className="ed-band ed-band--ink ed-pad">
        <div className="ed-container ed-cta">
          <h2 className="ed-cta__t">Start with confidence — no registration fee.</h2>
          <div className="ed-cta__row">
            <Link to="/candidates/apply" className="d13-btn d13-btn--primary">Submit your CV <ArrowRight size={16} strokeWidth={2.2} /></Link>
            <Link to="/employers/brief" className="d13-btn d13-btn--ghost">Request workers</Link>
          </div>
        </div>
      </section>

      <D13Footer />
    </LiveLayout>
  );
}
